import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";

import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { fetchAllAlertsData } from "../../../../api/userAlerts";
import MuiLinearProgressLoader from "../../../muiComps/MuiLinearProgressLoader";
import toast from "react-hot-toast";
import ReactHotToaster from "../../../common/ReactHotToaster";
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import axios from "axios";
import { axiosConfig } from "../../../../axios/axiosConfig";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// TABLE HEADINGS
const headCells = [
  {
    id: "serial",
    numeric: true,
    disablePadding: true,
    label: "Sr.No.",
  },
  {
    id: "symbol",
    numeric: false,
    disablePadding: true,
    label: "SYMBOL",
  },
  {
    id: "alert_count",
    numeric: true,
    disablePadding: true,
    label: "ALERT COUNT",
  },
  {
    id: "price_alert_count",
    numeric: true,
    disablePadding: true,
    label: "PRICE ALERT",
  },
  {
    id: "buy_alert_count",
    numeric: true,
    disablePadding: true,
    label: "BUY ALERT",
  },
  {
    id: "sell_alert_count",
    numeric: true,
    disablePadding: true,
    label: "SELL ALERT",
  },
];

// const headCells2 = [
//   {
//     id: 'name',
//     numeric: false,
//     disablePadding: true,
//     label: 'Dessert (100g serving)',
//   },
//   {
//     id: 'calories',
//     numeric: true,
//     disablePadding: false,
//     label: 'Calories',
//   },
//   {
//     id: 'fat',
//     numeric: true,
//     disablePadding: false,
//     label: 'Fat (g)',
//   },
//   {
//     id: 'carbs',
//     numeric: true,
//     disablePadding: false,
//     label: 'Carbs (g)',
//   },
//   {
//     id: 'protein',
//     numeric: true,
//     disablePadding: false,
//     label: 'Protein (g)',
//   },
// ];

function EnhancedTableHead(props) {
  const {
    // onSelectAllClick,
    order,
    orderBy,
    // numSelected,
    // rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ fontWeight: 700, padding: "12px 16px", color: "#fff", background: "#04343a", borderBottom: "none" }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              sx={{
                color: "#fff !important",
                "& .MuiTableSortLabel-icon": { color: "rgba(255,255,255,0.7) !important" },
              }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected } = props;

  // const headerStyles = {
  //   backgroundColor: "#F5B7B1",
  //   borderRadius: "2px",
  //   padding: "2px 4px",
  // };

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {/* {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Alert Count for All Symbols
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )} */}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

export default function AdminAlertsTable() {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const {
    user: { userDetails },
  } = useSelector((store) => store.auth);

  // ALL USER ALERTS DATA FOR SUPER ADMINs
  const [allSymbolAlertsData, setAllSymbolAlertsData] = useState([]);
  const [alertCount, setAlertCount] = useState({});
  const { active_total_alert_count, total_alert_count } = alertCount;

  // const formattedAllSymbolAlertsData = allSymbolAlertsData.map((item) => {
  //   return {
  //     alert_count: item.alert_count,
  //     ...item.data,
  //   };
  // });
  useEffect(() => {
    if (userDetails?.id) {
      fetchAlertsData();
    }
  }, [userDetails?.id]);
  const fetchAlertsData = async () => {
    try {
      setIsLoading(true);
      setIsError(false);

      const response = await axiosConfig({
        method: "get",
        url: "/admin_alert",
        params: { userid: userDetails.id },
      });

      console.log("response", response?.data?.response);

      if (response.status === 200 && response?.data) {
        setAlertCount(response?.data);
        setAllSymbolAlertsData(response?.data?.response);
      }
    } catch (error) {
      console.error("Error fetching alerts:", error);
      setIsError(true);

      if (error.response?.status === 401) {
        console.warn(
          "Unauthorized — handled by interceptor (may refresh or logout)"
        );
      } else {
        toast.error(
          error?.response?.data?.response || "Failed to fetch alert data"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formattedAllSymbolAlertsData = allSymbolAlertsData
    .map((item) => ({
      alert_count: item.total_alert || 0,
      price_alert_count: item.price_alert_count || 0,
      buy_alert_count: item.buy_alert_count || 0,
      sell_alert_count: item.sell_alert_count || 0,
      symbol: item.symbol_name || "N/A",
    }))
    .filter((item) => !selectedSymbol || item.symbol === selectedSymbol);

  const uniqueSymbols = [
    ...new Set(formattedAllSymbolAlertsData.map((item) => item.symbol)),
  ];

  // // FETCH ALL ALERTS DATA
  // const userAlertsQuery = useQuery({
  //   queryKey: ["all-alerts"],
  //   queryFn: () => fetchAllAlertsData(userDetails.id),
  //   onSuccess: (data) => {
  //     setAlertCount(data);
  //     setAllSymbolAlertsData(JSON.parse(data.response));
  //   },
  //   onError: (error) => {
  //     toast.error(error?.response?.data?.response || "Something went wrong");
  //   },
  // });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // const handleSelectAllClick = (event) => {
  //   if (event.target.checked) {
  //     const newSelected = formattedAllSymbolAlertsData.map((n) => n.name);
  //     setSelected(newSelected);
  //     return;
  //   }
  //   setSelected([]);
  // };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0
      ? Math.max(
          0,
          (1 + page) * rowsPerPage - formattedAllSymbolAlertsData.length
        )
      : 0;

  // if (userAlertsQuery.isLoading) {
  //   return <MuiLinearProgressLoader />;
  // }

  return (
    <>
      {/* Stats + Search Bar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 3,
          p: "16px 20px",
          background: "#04343a",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600, fontSize: "14px" }}>
            Active Alerts:
          </Typography>
          <Typography sx={{ color: "#1db954", fontWeight: 700, fontSize: "16px" }}>
            {active_total_alert_count}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600, fontSize: "14px" }}>
            Total Alerts:
          </Typography>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "16px" }}>
            {total_alert_count}
          </Typography>
        </Box>

        <Autocomplete
          sx={{
            width: 280,
            ml: "auto",
            "& .MuiOutlinedInput-root": {
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: "8px",
              color: "#fff",
              "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
              "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
            },
            "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
            "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" },
          }}
          size="small"
          options={uniqueSymbols}
          value={selectedSymbol}
          onChange={(event, newValue) => {
            setSelectedSymbol(newValue || "");
          }}
          renderInput={(params) => (
            <TextField {...params} label="Search symbols" variant="outlined" />
          )}
          clearOnEscape
          isOptionEqualToValue={(option, value) => value === option}
        />
      </Box>

      {/* Table */}
      <Box sx={{ p: "0 0 8px 0" }}>
        <Paper elevation={0} sx={{ maxWidth: 1500, mb: 0 }}>
          <TableContainer sx = {{overflowX: "auto", overflowY: "auto", maxHeight: "65vh", border: "1px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)"}} 
          
          >
            <Table
            stickyHeader
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {stableSort(
                  formattedAllSymbolAlertsData,
                  getComparator(order, orderBy)
                )
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.symbol);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.symbol}
                        selected={isItemSelected}
                        sx={{ "&:hover": { background: "#f0faf5" } }}
                      >
                        <TableCell align="right" sx={{ color: "#64748b", fontSize: "13px" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row" padding="none">
                          <Link to={`/admin/alerts/${row.symbol}`} style={{ textDecoration: "none" }}>
                            <Typography
                              sx={{
                                color: "#016551",
                                fontWeight: 600,
                                fontSize: "14px",
                                "&:hover": { textDecoration: "underline" },
                                display: "inline-block",
                              }}
                            >
                              {row.symbol}
                            </Typography>
                          </Link>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{row.alert_count}</TableCell>
                        <TableCell align="right">{row?.price_alert_count}</TableCell>
                        <TableCell align="right" sx={{ color: "#016551" }}>{row?.buy_alert_count}</TableCell>
                        <TableCell align="right" sx={{ color: "#e53935" }}>{row?.sell_alert_count}</TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={formattedAllSymbolAlertsData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <ReactHotToaster />
      </Box>
    </>
  );
}
